import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Loader2, type LucideIcon } from 'lucide-react'
import { cn } from '@/lib/cn'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'subtle'
export type ButtonSize = 'sm' | 'md'

const VARIANTS: Record<ButtonVariant, string> = {
  // Solid orange, reserved for the one action a panel is really for.
  primary: 'bg-accent text-accent-fg hover:bg-accent/90 active:bg-accent/80',
  // The workhorse: hairline outline on a raised panel, orange on hover.
  secondary:
    'border border-line bg-raised text-fg hover:border-accent/60 hover:text-accent',
  ghost: 'text-muted hover:bg-raised hover:text-fg',
  danger: 'bg-negative text-white hover:bg-negative/90 active:bg-negative/80',
  subtle: 'border border-negative/40 bg-negative/10 text-negative hover:bg-negative/20'
}

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1.5 rounded-md px-2.5 text-[11px] font-semibold uppercase tracking-label',
  md: 'h-9 gap-2 rounded-md px-3.5 text-[12.5px] font-medium'
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  icon?: LucideIcon
  /** Swaps the icon for a spinner and blocks input while a request is in flight. */
  loading?: boolean
  children?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon: Icon, loading, className, children, disabled, ...rest },
  ref
) {
  const iconOnly = !children
  const glyph = size === 'sm' ? 13 : 15
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-40',
        SIZES[size],
        iconOnly && (size === 'sm' ? 'w-8 px-0' : 'w-9 px-0'),
        VARIANTS[variant],
        className
      )}
      {...rest}
    >
      {loading ? (
        <Loader2 size={glyph} className="animate-spin" aria-hidden />
      ) : (
        Icon && <Icon size={glyph} aria-hidden />
      )}
      {children}
    </button>
  )
})
